/* eslint-disable */

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const LOGO_URL = "https://presentplus.uk/presentplus-logo-white.png";

export default function ActivateSchoolPage() {
  const [params] = useSearchParams();
  const agreementId = params.get("agreementId");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score;
  }, [password]);

  async function handleActivate(e) {
    e.preventDefault();

    setError("");
    setMessage("");

    if (!agreementId) {
      setError("Missing agreement ID.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const callable = httpsCallable(functions, "activateSchoolAdminAccount");

      await callable({
        agreementId,
        password,
      });

      setMessage("Account activated. Redirecting to login...");

      setTimeout(() => {
        window.location.href = "/login";
      }, 900);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not activate account.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <div style={styles.logoWrap}>
            <img src={LOGO_URL} alt="PresentPlus" style={styles.logo} />
          </div>
          <div>
            <div style={styles.brand}>PresentPlus</div>
            <div style={styles.brandSub}>School administrator activation</div>
          </div>
        </div>

        <h1 style={styles.title}>Create your administrator password.</h1>

        <p style={styles.text}>
          Your school agreement has been signed. Create your administrator
          password, then sign in to complete MFA and access the school dashboard.
        </p>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
        {message ? <div style={styles.successBox}>{message}</div> : null}

        <form style={styles.form} onSubmit={handleActivate}>
          <label style={styles.field}>
            <span>New password</span>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter a secure password"
            />
          </label>

          <label style={styles.field}>
            <span>Confirm password</span>
            <input
              style={styles.input}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
            />
          </label>

          <div style={styles.strengthBox}>
            <div style={styles.strengthLabel}>Password strength</div>
            <div style={styles.strengthTrack}>
              <div
                style={{
                  ...styles.strengthFill,
                  width: `${Math.max(10, strength * 25)}%`,
                }}
              />
            </div>
            <div style={styles.strengthText}>
              Use at least 8 characters, including a number, capital letter, and
              symbol.
            </div>
          </div>

          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading ? "Activating..." : "Activate Account"}
          </button>
        </form>

        <div style={styles.footerNote}>
          After activation, MFA setup will be enforced at login for admin security.
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 24,
    background:
      "radial-gradient(circle at top right, rgba(124,58,237,0.18), transparent 30%), linear-gradient(135deg,#eef5ff 0%,#f8fbff 55%,#f3e8ff 100%)",
  },
  card: {
    width: "100%",
    maxWidth: 760,
    borderRadius: 36,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    boxShadow: "0 30px 90px rgba(15,23,42,0.12)",
    padding: 42,
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 24,
  },
  logoWrap: {
    width: 54,
    height: 54,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#2563eb,#7c3aed)",
    boxShadow: "0 18px 38px rgba(79,70,229,0.22)",
  },
  logo: {
    width: 34,
    height: 34,
    objectFit: "contain",
  },
  brand: {
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.04em",
    color: "#0f172a",
  },
  brandSub: {
    marginTop: 3,
    color: "#64748b",
    fontWeight: 850,
    fontSize: 13,
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 46,
    lineHeight: 1,
    letterSpacing: "-0.06em",
    fontWeight: 950,
  },
  text: {
    marginTop: 16,
    color: "#475569",
    fontSize: 16,
    lineHeight: 1.75,
  },
  form: {
    marginTop: 26,
    display: "grid",
    gap: 16,
  },
  field: {
    display: "grid",
    gap: 8,
    color: "#0f172a",
    fontWeight: 900,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid #cfe0ff",
    background: "#ffffff",
    padding: "15px 16px",
    fontSize: 15,
    color: "#0f172a",
    outline: "none",
  },
  strengthBox: {
    borderRadius: 22,
    background: "#f8fbff",
    border: "1px solid #dbeafe",
    padding: 16,
  },
  strengthLabel: {
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.11em",
    color: "#64748b",
    marginBottom: 10,
  },
  strengthTrack: {
    height: 9,
    borderRadius: 999,
    background: "#e2e8f0",
    overflow: "hidden",
  },
  strengthFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(135deg,#2563eb,#7c3aed)",
  },
  strengthText: {
    marginTop: 10,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.5,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 18,
    padding: "16px 22px",
    background: "linear-gradient(135deg,#2563eb 0%,#7c3aed 100%)",
    color: "#ffffff",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 18px 40px rgba(79,70,229,0.24)",
  },
  errorBox: {
    marginTop: 18,
    borderRadius: 18,
    padding: 14,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontWeight: 850,
  },
  successBox: {
    marginTop: 18,
    borderRadius: 18,
    padding: 14,
    background: "#ecfdf3",
    border: "1px solid #bbf7d0",
    color: "#166534",
    fontWeight: 850,
  },
  footerNote: {
    marginTop: 22,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
  },
};